import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['completed', 'total', 'percentage', 'progress', 'bar']
  static values = {
    selector: String,
  }

  declare readonly hasBarTarget: boolean
  declare readonly hasPercentageTarget: boolean
  declare readonly hasProgressTarget: boolean

  private completedTarget!: HTMLElement
  private totalTarget!: HTMLElement
  private percentageTarget?: HTMLElement
  private progressTarget?: HTMLElement
  private barTarget?: HTMLElement
  private selectorValue!: string

  connect(): void {
    this.initCompleted()
    this.updateProgress()
    this.addCustomEventListeners()
  }

  private addCustomEventListeners(): void {
    // Listen for the update event from toggle.js
    this.element.addEventListener(
      'ledger-check-list--progress:updateSummaryUser',
      ((event: CustomEvent<{ completed: boolean }>) => {
        this.updateCompletedCount(event.detail.completed)
        this.updateProgressBar()
      }) as EventListener
    )

    this.element.addEventListener(
      'ledger-check-list--progress:updateSummary',
      ((_event: CustomEvent) => {
        this.initCompleted()
        this.updateProgress()
      }) as EventListener
    )
  }

  private initCompleted(): void {
    const completedCount = document.querySelectorAll<HTMLElement>(
      `${this.selectorValue}[data-completed="true"]`
    ).length

    this.completedTarget.textContent = completedCount.toString()
  }

  // Update the completed count based on whether we're adding or subtracting
  private updateCompletedCount(completed: boolean): void {
    let completedCount = parseInt(this.completedTarget.textContent || '0', 10)

    // If adding to count, increment; otherwise decrement (but not below 0)
    completedCount = completed
      ? completedCount + 1
      : Math.max(0, completedCount - 1)

    this.completedTarget.textContent = completedCount.toString()
  }

  private updateProgress(): void {
    this.updatePercentage()
    this.updateProgressBar()
  }

  private updatePercentage(): void {
    if (!this.hasPercentageTarget) {
      return
    }

    const completedCount = parseInt(this.completedTarget.textContent || '0', 10)
    const totalCount = parseInt(this.totalTarget.textContent || '1', 10)
    const percentage = Math.round((completedCount / totalCount) * 100)

    if (this.percentageTarget) {
      this.percentageTarget.textContent = `${percentage}%`
    }
  }

  private updateProgressBar(): void {
    if (!this.hasProgressTarget || !this.hasBarTarget) {
      return
    }

    const completedCount = parseInt(this.completedTarget.textContent || '0', 10)
    const totalCount = parseInt(this.totalTarget.textContent || '1', 10)
    const percentage = Math.round((completedCount / totalCount) * 100)

    const progressClass = this.calculateProgressClass(percentage)

    if (this.barTarget) {
      this.barTarget.style.width = `${percentage}%`
    }

    if (this.progressTarget) {
      this.progressTarget.className = `progress ${progressClass}`
    }
  }

  private calculateProgressClass(percentage: number): string {
    if (percentage < 31) {
      return 'progress-danger'
    } else if (percentage < 71) {
      return 'progress-warning'
    }

    return 'progress-success'
  }
}
